import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import { getGlobalSettings } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { BookingDocumentsNames } from 'app/appState/model/types';

const COOKIE_CONSENT_KEY = 'cookie_consent_accepted';

export const CookieConsent: FC = memo(() => {
  const globalSettings = useAppSelector(getGlobalSettings);
  const { t } = useAppTranslation('common');
  const { isDesktop } = useScreenBreakpoints();

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(COOKIE_CONSENT_KEY);

    if (!accepted) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const privacyPolicyLink = useMemo(
    () => globalSettings?.bookingDocuments.find((el) => el.docName === BookingDocumentsNames.PRIVACY_POLICY)?.link || '#',
    [globalSettings],
  );

  const personalDataAgreementLink = useMemo(
    () => globalSettings?.bookingDocuments.find((el) => el.docName === BookingDocumentsNames.PERSONAL_DATA_AGREEMENT)?.link || '#',
    [globalSettings],
  );

  const handleAccept = (): void => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');

    setIsVisible(false);
    document.body.style.overflow = '';
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div
        className="mx-4 w-full max-w-[720px] rounded-2xl p-6 shadow-2xl"
        style={{
          background: globalSettings?.themeColors.primary,
        }}
      >
        <div className={`flex ${isDesktop ? 'flex-row items-center gap-6' : 'flex-col gap-4'}`}>
          <div className="flex-1 text-white">
            <p className="text-sm leading-6 desktop:text-lg">
              {t('cookieConsent.text.before')}{' '}
              <a href={privacyPolicyLink} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">
                {t('cookieConsent.privacyPolicy')}
              </a>{' '}
              {t('cookieConsent.text.and')}{' '}
              <a href={personalDataAgreementLink} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">
                {t('cookieConsent.personalDataAgreement')}
              </a>
              .
            </p>
          </div>

          <button
            type="button"
            onClick={handleAccept}
            className="min-w-[160px] rounded-md px-5 py-3 font-medium transition-opacity hover:opacity-90"
            style={{
              background: globalSettings?.themeColors.accent,
              color: globalSettings?.themeColors.white,
              fontSize: isDesktop ? '18px' : '14px',
            }}
          >
            {t('Accept')}
          </button>
        </div>
      </div>
    </div>
  );
});
